import apiClient, { extractData, handleApiError } from './api.js';

export class OrderService {
  /**
   * Get the next available order number for an outlet
   * GET /api/v1/orders/next-number/{outlet_id}
   * @param {string} outletId
   * @returns {Promise<number>}
   */
  static async getNextOrderNumber(outletId) {
    try {
      const response = await apiClient.get(`/orders/next-number/${outletId}`);
      const data = extractData(response);
      return data.next_order_number;
    } catch (error) {
      console.error('Error getting next order number:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Create a new order from a cart
   * POST /api/v1/orders
   * @param {Object} orderData - { cart_list, user_id, outlet_id, status? }
   * @returns {Promise<{order_id: string, order_num: number}>}
   */
  static async createOrder(orderData) {
    try {
      const response = await apiClient.post('/orders', {
        cart_list: orderData.cart_list || orderData.cartList,
        user_id: orderData.user_id || orderData.userId,
        outlet_id: orderData.outlet_id || orderData.outletId,
        status: orderData.status || 'Active',
      });
      return extractData(response);
    } catch (error) {
      console.error('Error creating order:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Fetch orders filtered by status and outlet (returns grouped order objects)
   * GET /api/v1/orders/by-status?status=...&outlet_id=...
   * @param {string} outletId
   * @param {string} [status] - "Active", "Processing", "Completed"
   * @returns {Promise<Array>}
   */
  static async fetchOrders(outletId, status) {
    try {
      const params = { outlet_id: outletId };
      if (status) params.status = status;

      const response = await apiClient.get('/orders/by-status', { params });
      const orders = extractData(response) || [];

      // The backend returns PascalCase fields (OrderID, OrderNum, Amount, etc.)
      // per the API spec. Map with fallbacks for both formats.
      return orders.map((order) => ({
        OrderNum: order.OrderNum ?? order.order_num,
        Amount: order.Amount ?? order.total_amount ?? 0,
        ItemNames: order.ItemNames || order.items?.map((i) => i.item_name) || [],
        Quantity: order.Quantity || order.items?.map((i) => i.quantity) || [],
        Status: order.Status || order.status,
        OrderID: order.OrderID || order.order_id,
        ProductIDs: order.ProductIDs || order.items?.map((i) => i.item_id) || [],
        CreatedAt: order.CreatedAt || order.created_at,
        UserID: order.UserID || order.user_id,
        OutletID: order.OutletID || order.outlet_id,
        items: order.items || [],
      }));
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Update the status of an order
   * PATCH /api/v1/orders/{order_id}/status
   * @param {string} orderId
   * @param {string} status - "Active", "Processing", "Completed", "Cancelled"
   * @returns {Promise<boolean>}
   */
  static async updateOrderStatus(orderId, status) {
    try {
      await apiClient.patch(`/orders/${orderId}/status`, { status });
      return true;
    } catch (error) {
      console.error('Error updating order status:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Batch update order statuses
   * PATCH /api/v1/orders/status/batch
   * @param {string[]} orderIds
   * @param {string} status
   * @returns {Promise<boolean>}
   */
  static async batchUpdateOrderStatus(orderIds, status) {
    try {
      await apiClient.patch('/orders/status/batch', {
        order_ids: orderIds,
        status,
      });
      return true;
    } catch (error) {
      console.error('Error batch updating order statuses:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Fetch top selling food items for an outlet
   * GET /api/v1/orders/top-items/{outlet_id}
   * @param {string} outletId
   * @returns {Promise<Array>}
   */
  static async fetchTopFoodItems(outletId) {
    try {
      const response = await apiClient.get(`/orders/top-items/${outletId}`);
      const items = extractData(response) || [];

      // The API returns: item_name, item_id, total_quantity, total_revenue, order_count
      return items.map((item) => ({
        item_name: item.item_name,
        item_id: item.item_id,
        total_quantity: item.total_quantity,
        total_revenue: item.total_revenue || item.total_amount || 0,
        total_amount: item.total_revenue || item.total_amount || 0,
        order_count: item.order_count || 0,
      }));
    } catch (error) {
      console.error('Error fetching top food items:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Fetch completed orders
   * GET /api/v1/orders/complete?outlet_id=...
   * @param {string} outletId
   * @returns {Promise<Array>}
   */
  static async fetchCompleteOrders(outletId) {
    try {
      const response = await apiClient.get('/orders/complete', {
        params: { outlet_id: outletId },
      });
      const orders = extractData(response) || [];

      // The API returns camelCase fields: orderID, orderAmount, orderItemsList,
      // orderItemsIdsList, createdTime/timestamp, outletId, userId, status
      // Map with fallbacks for both camelCase and snake_case formats.
      return orders.map((order) => {
        const itemNames = order.orderItemsList
          || (order.orderItemsString ? order.orderItemsString.split(', ') : null)
          || (order.item_names ? order.item_names.split(', ') : []);

        const productIds = order.orderItemsIdsList
          || (order.orderItemsIdsString ? order.orderItemsIdsString.split(', ') : null)
          || (order.product_ids ? order.product_ids.split(', ') : []);

        return {
          OrderNum: order.orderNum || order.order_num || order.OrderNum || 0,
          Amount: order.orderAmount ?? order.order_amount ?? order.Amount ?? 0,
          ItemNames: itemNames,
          Quantity: [],
          Status: order.status || order.Status,
          OrderID: order.orderID || order.order_id || order.OrderID || order.id,
          ProductIDs: productIds,
          UserID: order.userId || order.user_id || order.UserID,
          OutletID: order.outletId || order.outlet_id || order.OutletID,
          CreatedAt: order.createdTime || order.timestamp || order.created_at || order.CreatedAt,
        };
      });
    } catch (error) {
      console.error('Error fetching complete orders:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Create a completed order entry directly
   * POST /api/v1/orders/complete
   * @param {Object} orderData
   * @returns {Promise<boolean>}
   */
  static async postOrder(orderData) {
    try {
      await apiClient.post('/orders/complete', {
        order_id: orderData.order_id || orderData.orderID,
        item_names: orderData.item_names || (orderData.orderItemsList || []).join(', '),
        product_ids: orderData.product_ids || (orderData.orderItemsIdsList || []).join(', '),
        order_amount: orderData.order_amount || orderData.orderAmount,
        outlet_id: orderData.outlet_id || orderData.outletId,
        user_id: orderData.user_id || orderData.userId,
        status: orderData.status || 'Completed',
      });
      return true;
    } catch (error) {
      console.error('Error posting order:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get line items for an order (for editing)
   * GET /api/v1/orders/{order_id}/items
   * @param {string} orderId
   * @returns {Promise<Array>}
   */
  static async fetchOrderItems(orderId) {
    try {
      const response = await apiClient.get(`/orders/${orderId}/items`);
      return extractData(response) || [];
    } catch (error) {
      console.error('Error fetching order items:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Mark all items in an order as "Updated"
   * PATCH /api/v1/orders/{order_id}/mark-updated
   * @param {string} orderId
   * @returns {Promise<boolean>}
   */
  static async markOrderUpdated(orderId) {
    try {
      await apiClient.patch(`/orders/${orderId}/mark-updated`);
      return true;
    } catch (error) {
      console.error('Error marking order as updated:', error);
      throw new Error(handleApiError(error));
    }
  }
}
